/**
 * ColorPicker - Custom RJSF widget for color selection
 *
 * Displays a grid of color swatches from the node color palettes.
 * Used in PropertyPanel for sticky note and shape colors.
 *
 * Features:
 * - Grid layout of color swatches
 * - Visual selection indicator (ring/border)
 * - Accessibility labels for screen readers
 * - Supports disabled/readonly states
 */
import type { WidgetProps } from '@rjsf/utils';
import { STICKY_COLORS } from '../nodes/StickyNoteNode';
import { SHAPE_COLORS } from '../nodes/ShapeNode';

// ============================================================================
// COLOR CONFIGURATION
// ============================================================================

/**
 * Combined color palette from all node types
 *
 * Merges sticky note colors (pastel) and shape colors (more saturated)
 * into a single palette for the color picker widget.
 */
const ALL_COLORS = {
  ...STICKY_COLORS,
  ...SHAPE_COLORS,
};

/**
 * Human-readable color names for accessibility and tooltips
 *
 * Maps hex values to descriptive names. Used for:
 * - title attributes (hover tooltips)
 * - aria-label for screen readers
 */
const COLOR_NAMES: Record<string, string> = {
  // Sticky note colors (Material Design 200-level)
  '#fef08a': 'Yellow',
  '#fed7aa': 'Orange',
  '#bbf7d0': 'Green',
  '#bfdbfe': 'Blue',
  '#ddd6fe': 'Purple',
  '#fbcfe8': 'Pink',
  // Shape colors (Material Design 300-level)
  '#93c5fd': 'Light Blue',
  '#86efac': 'Light Green',
  '#fca5a5': 'Light Red',
  '#fde047': 'Bright Yellow',
  '#c4b5fd': 'Lavender',
  '#d1d5db': 'Gray',
};

// ============================================================================
// COLOR PICKER WIDGET (RJSF Integration)
// ============================================================================

/**
 * ColorPicker - RJSF widget for selecting colors
 *
 * This component implements the RJSF WidgetProps interface, allowing it to be
 * used as a custom widget in JSON Schema forms.
 *
 * Usage in UI Schema:
 * ```json
 * {
 *   "color": {
 *     "ui:widget": "ColorWidget"
 *   }
 * }
 * ```
 *
 * @param props - RJSF WidgetProps containing value, onChange, disabled, readonly
 */
export function ColorPicker(props: WidgetProps) {
  const { value, onChange, disabled, readonly } = props;

  // Get all available colors as [name, hex] pairs
  const colors = Object.entries(ALL_COLORS);

  return (
    <div className="grid grid-cols-6 gap-2 p-2">
      {colors.map(([name, hex]) => (
        <button
          key={hex}
          type="button"
          disabled={disabled || readonly}
          onClick={() => onChange(hex)}
          className={`
            w-8 h-8 rounded-lg border-2 transition-all
            ${
              // Selected state: blue border with ring
              value === hex
                ? 'border-blue-500 ring-2 ring-blue-200'
                : 'border-gray-200 hover:border-gray-400'
            }
            ${
              // Disabled state: reduced opacity, no pointer
              disabled || readonly
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer'
            }
          `}
          style={{ backgroundColor: hex }}
          title={COLOR_NAMES[hex] || name}
          aria-label={`Select ${COLOR_NAMES[hex] || name} color`}
        />
      ))}
    </div>
  );
}

// ============================================================================
// COLOR SWATCH COMPONENT (Standalone Usage)
// ============================================================================

/**
 * ColorSwatch - Simple color preview component
 *
 * Use this for displaying a color outside of RJSF forms, such as:
 * - In the property panel header to show current color
 * - In node lists or search results
 *
 * @param color - Hex color value (e.g., '#fef08a')
 * @param size - Size variant: 'sm' (16px), 'md' (24px), 'lg' (32px)
 */
export function ColorSwatch({
  color,
  size = 'md',
}: {
  color: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  // Map size prop to Tailwind classes
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  return (
    <span
      className={`inline-block rounded border border-gray-300 ${sizeClasses[size]}`}
      style={{ backgroundColor: color }}
    />
  );
}
